import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { StorefrontIcon as Storefront, ShieldWarningIcon as ShieldWarning, ArrowCounterClockwiseIcon as ArrowCounterClockwise, TrashIcon as Trash, ClockCounterClockwiseIcon as ClockCounterClockwise, EyeIcon as Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/**
 * /admin/storefronts — Storefront moderation console.
 *
 * Single screen for the platform owner to see every Kennel Storefront
 * at a glance (status, product count, revenue), suspend a violating
 * one with a typed reason, reinstate, or permanently nuke. Includes
 * an audit log tab so every action has a paper trail.
 */
export default function AdminStorefronts() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [target, setTarget] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/storefronts");
      setStores(data.stores || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const filtered = stores.filter((s) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (q && !`${s.name} ${s.slug} ${s.owner_email || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <AppShell>
      <Helmet><title>Storefront moderation — Admin</title></Helmet>
      <div className="max-w-6xl mx-auto p-6" data-testid="admin-storefronts">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-crimson">
          <ShieldWarning size={14} weight="duotone" /> Admin · Moderation
        </div>
        <h1 className="font-spectral text-4xl text-ink mt-3">The Dog Shop</h1>
        <p className="text-[#6B6B6B] mt-1 text-sm">Suspend, reinstate, or remove storefronts that violate the Seller Agreement.</p>

        <Tabs defaultValue="stores" className="mt-6">
          <TabsList>
            <TabsTrigger value="stores" data-testid="tab-stores">Stores</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
          </TabsList>

          <TabsContent value="stores" className="py-6">
            <div className="flex gap-2 flex-wrap items-center mb-4">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, slug, or owner email"
                className="max-w-sm"
                data-testid="store-search"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-full border border-[#E5E5E5] bg-white text-[12px] px-3 h-9"
                data-testid="status-filter"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="deleted">Deleted</option>
              </select>
              <span className="text-[12px] text-[#6B6B6B] ml-auto">{filtered.length} of {stores.length}</span>
            </div>

            <div className="rounded-xl border border-[#E8E0CC] bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#6B6B6B]">
                  <tr>
                    <th className="text-left px-3 py-2">Store</th>
                    <th className="text-left px-3 py-2">Owner</th>
                    <th className="text-right px-3 py-2">Products</th>
                    <th className="text-right px-3 py-2">Paid orders</th>
                    <th className="text-right px-3 py-2">Revenue</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="py-6 text-center text-[#6B6B6B]">Loading…</td></tr>}
                  {!loading && filtered.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-[#6B6B6B]">No storefronts match.</td></tr>}
                  {filtered.map((s) => (
                    <tr key={s.id} className="border-t border-[#F1ECDD] hover:bg-[#FAFAFA]" data-testid={`store-row-${s.slug}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Storefront size={14} weight="duotone" className="text-gold-dark" />
                          <span className="font-medium">{s.name}</span>
                        </div>
                        <a href={`/store/${s.slug}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#6B6B6B] underline">/store/{s.slug}</a>
                      </td>
                      <td className="px-3 py-2 text-[12px] text-[#3a3a3a]">{s.owner_email}</td>
                      <td className="px-3 py-2 text-right font-tabular">{s.product_count}</td>
                      <td className="px-3 py-2 text-right font-tabular">{s.paid_order_count}</td>
                      <td className="px-3 py-2 text-right font-tabular">${((s.paid_revenue_cents || 0) / 100).toFixed(2)}</td>
                      <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => setTarget(s)} className="rounded-full h-7 text-[11px]">
                          Manage
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="py-6"><AuditLogTab /></TabsContent>
        </Tabs>

        <ManageStoreDialog store={target} onClose={() => setTarget(null)} onDone={reload} />
      </div>
    </AppShell>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "Active", cls: "bg-emerald-100 text-emerald-800" },
    suspended: { label: "Suspended", cls: "bg-amber-100 text-amber-800" },
    deleted: { label: "Deleted", cls: "bg-gray-200 text-gray-700" },
  };
  const v = map[status] || map.active;
  return <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${v.cls}`}>{v.label}</span>;
}

function ManageStoreDialog({ store, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => { if (store) { setReason(store.suspension_reason || ""); setConfirmDelete(false); } }, [store]);

  if (!store) return null;

  const act = async (path, method = "post", body) => {
    setBusy(true);
    try {
      await api[method](`/admin/storefronts/${store.id}${path}`, body);
      toast.success("Done");
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!store} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="manage-store-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Storefront size={20} weight="duotone" className="text-gold-dark" /> {store.name}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            <a href={`/store/${store.slug}`} target="_blank" rel="noreferrer" className="underline">{`/store/${store.slug}`}</a>
            {" · "}<span>{store.owner_email}</span>{" · "}<StatusBadge status={store.status} />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-[13px]">
          {store.status === "suspended" && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="font-semibold text-amber-900">Currently suspended</p>
              <p className="text-amber-900 mt-1">Reason on file: <em>{store.suspension_reason || "—"}</em></p>
              <p className="text-amber-800 text-[11px] mt-1">Pending-payment orders were cancelled at suspension time.</p>
            </div>
          )}
          {store.status === "active" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider">Suspension reason</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. Listed a prohibited product (CBD treats). Asked to remove via inbox 2026-02-03."
                data-testid="suspend-reason"
              />
              <p className="text-[10px] text-[#6B6B6B] mt-1">
                Suspending will hide the storefront, cancel pending orders, log this action, and notify the owner via inbox.
                Remember to pause Stripe payouts manually in the Connect dashboard.
              </p>
            </div>
          )}

          <div className="flex gap-2 flex-wrap pt-2 border-t border-[#EEE]">
            {store.status === "active" && (
              <Button
                onClick={() => act("/suspend", "post", { reason: reason.trim() })}
                disabled={busy || reason.trim().length < 4}
                className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="suspend-btn"
              >
                <ShieldWarning size={14} className="mr-1" /> Suspend
              </Button>
            )}
            {store.status === "suspended" && (
              <Button
                onClick={() => act("/unsuspend", "post")}
                disabled={busy}
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="unsuspend-btn"
              >
                <ArrowCounterClockwise size={14} className="mr-1" /> Reinstate
              </Button>
            )}
            {store.status !== "deleted" && (
              confirmDelete ? (
                <Button
                  onClick={() => act("", "delete")}
                  disabled={busy}
                  className="rounded-full bg-crimson hover:bg-red-700 text-white"
                  data-testid="confirm-delete-btn"
                >
                  <Trash size={14} className="mr-1" /> Confirm permanent delete
                </Button>
              ) : (
                <Button
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full border-crimson text-crimson hover:bg-crimson/5"
                  data-testid="delete-btn"
                >
                  <Trash size={14} className="mr-1" /> Permanently delete…
                </Button>
              )
            )}
            <Button onClick={onClose} disabled={busy} variant="ghost" className="rounded-full ml-auto">Close</Button>
          </div>
          {confirmDelete && (
            <p className="text-[11px] text-crimson">
              This removes the storefront and all products from public view. Paid order receipts remain visible to buyers. This action cannot be undone.
            </p>
          )}
        </div>

        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}

function AuditLogTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/storefronts/audit-log")
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, []);

  const kindLabel = {
    storefront_suspend: { label: "Suspended", cls: "text-amber-700" },
    storefront_unsuspend: { label: "Reinstated", cls: "text-emerald-700" },
    storefront_delete: { label: "Deleted", cls: "text-crimson" },
  };

  return (
    <div className="rounded-xl border border-[#E8E0CC] bg-white" data-testid="audit-log">
      <table className="w-full text-sm">
        <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#6B6B6B]">
          <tr>
            <th className="text-left px-3 py-2">When</th>
            <th className="text-left px-3 py-2">Action</th>
            <th className="text-left px-3 py-2">Store</th>
            <th className="text-left px-3 py-2">Reason / details</th>
            <th className="text-left px-3 py-2">Admin</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={5} className="py-6 text-center text-[#6B6B6B]">Loading…</td></tr>}
          {!loading && entries.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#6B6B6B]">No actions logged yet.</td></tr>}
          {entries.map((e) => {
            const k = kindLabel[e.kind] || { label: e.kind, cls: "text-[#3a3a3a]" };
            return (
              <tr key={e.id} className="border-t border-[#F1ECDD]">
                <td className="px-3 py-2 text-[12px] text-[#6B6B6B]"><ClockCounterClockwise size={12} className="inline mr-1" />{new Date(e.created_at).toLocaleString()}</td>
                <td className={`px-3 py-2 font-semibold ${k.cls}`}>{k.label}</td>
                <td className="px-3 py-2">{e.store_slug || e.store_id}</td>
                <td className="px-3 py-2 text-[12px] max-w-md">
                  {e.reason || (
                    <span className="text-[#6B6B6B]">
                      {e.cancelled_pending_orders != null && `· cancelled ${e.cancelled_pending_orders} pending orders`}
                      {e.products_removed != null && ` · removed ${e.products_removed} products`}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-[12px]">{e.admin_email || e.admin_id}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
